import type { ReloadSafety } from './reload-blockers';

/**
 * A chunk can fail to load without any deploy (a dropped or aborted fetch), so
 * the boundary reloads once per path per tab session when nothing would be
 * lost (#6041). A repeat, or any unsafe state, keeps the manual update screen.
 */
const CHUNK_RELOAD_PREFIX = 'imai:chunk-reload:';

const CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
  /ChunkLoadError/,
  /Loading (?:CSS )?chunk [\w-]+ failed/,
  /Failed to load chunk /,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
];

export type ChunkLoadRecovery = 'reloaded' | 'repeat' | 'unsafe' | 'storage';

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  if ('name' in error && error.name === 'ChunkLoadError') return true;
  const message = 'message' in error ? error.message : null;
  return (
    typeof message === 'string' &&
    CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
}

/**
 * Safety read when the boundary rendered is the route's own (its reload owners
 * are still mounted). After the crashed route unmounts the registry can only
 * say `unknown`, so the snapshot decides and the live value may only veto.
 */
export function resolveChunkReloadSafety(
  atCrash: ReloadSafety,
  current: ReloadSafety
): ReloadSafety {
  if (atCrash !== 'safe') return atCrash;
  return current === 'blocked' ? 'blocked' : 'safe';
}

export function tryReloadForChunkError({
  path,
  safety,
  storage,
  reload,
}: {
  path: string;
  safety: ReloadSafety;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
}): ChunkLoadRecovery {
  const key = `${CHUNK_RELOAD_PREFIX}${path}`;
  try {
    if (storage.getItem(key)) return 'repeat';
    if (safety !== 'safe') return 'unsafe';
    storage.setItem(key, '1');
  } catch {
    return 'storage';
  }
  reload();
  return 'reloaded';
}
